import React, { useState, useEffect } from "react";
import { AlertCircle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  children: React.ReactNode;
}

export function ErrorBoundary({ children }: Props) {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      setHasError(true);
      setError(event.error);
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  const handleReset = () => {
    setHasError(false);
    setError(null);
    window.location.href = "/";
  };

  if (hasError) {
    let errorMessage = "Ha ocurrido un error inesperado.";
    let isPermissionError = false;

    try {
      if (error?.message) {
        const parsed = JSON.parse(error.message);
        if (parsed.error && parsed.error.includes("insufficient permissions")) {
          errorMessage = "No tienes permisos suficientes para realizar esta acción.";
          isPermissionError = true;
        }
      }
    } catch (e) {
      // Not a JSON error message
    }

    return (
      <div className="flex min-h-[400px] w-full items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-6 w-6" />
              <CardTitle>¡Ups! Algo salió mal</CardTitle>
            </div>
            <CardDescription>
              {isPermissionError 
                ? "Parece que intentaste acceder a algo restringido o tu sesión ha expirado."
                : "La aplicación ha encontrado un problema y no puede continuar."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md bg-muted p-4">
              <p className="text-sm font-mono break-all text-muted-foreground">
                {errorMessage}
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={handleReset} className="w-full gap-2">
              <RefreshCcw className="h-4 w-4" />
              Volver al inicio
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
